
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSelector from './LanguageSelector';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t, language } = useLanguage();
  const location = useLocation();

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Function to scroll to top
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const bookingUrl = language === 'el' 
    ? "https://loenaluxurysuites.reserve-online.net/?lang=el"
    : "https://loenaluxurysuites.reserve-online.net/?lang=en";

  const navItems = [
    { href: '/', label: t('home') },
    { href: '/suite/luxury-suite', label: t('suites') },
    { href: '/journal', label: t('blog') },
    { href: '/reviews', label: t('reviews') },
    { href: '/contact', label: t('contact') },
    { href: '/about', label: t('about') },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-black/95 backdrop-blur-md border-b border-gray-800' 
        : 'bg-black/50 backdrop-blur-sm'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2" onClick={scrollToTop}>
            <img 
              src="/lovable-uploads/686591f9-3fb2-4efe-b292-80506550bc8a.png" 
              alt="Loena Luxury Suites Logo" 
              className="h-8 w-auto"
              {...({ fetchpriority: "high" } as any)}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={scrollToTop}
                className="text-white hover:text-loena-gold transition-colors duration-200 font-medium"
              >
                {item.label}
              </Link>
            ))}
            
            {/* Language Selector - Desktop */}
            <div className="hidden lg:block">
              <LanguageSelector />
            </div>
            
            {/* Book Now Button - Desktop */}
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-loena-gold/90 text-black px-6 py-2 rounded-md font-semibold hover:bg-loena-gold transition-colors duration-200"
            >
              {t('bookNow')}
            </a>
          </div>

          {/* Mobile controls - Language Selector and Book Now Button */}
          <div className="lg:hidden flex items-center space-x-1">
            {/* Language Selector - Mobile */}
            <div className="hidden sm:block">
              <LanguageSelector />
            </div>
            
            {/* Book Now Button - Mobile */}
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-loena-gold/90 text-black px-2 py-1 text-xs sm:px-3 sm:py-1.5 sm:text-sm rounded-md font-semibold hover:bg-loena-gold transition-colors duration-200 whitespace-nowrap"
            >
              {t('bookNow')}
            </a>
            
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-white hover:text-loena-gold transition-colors ml-1 p-1"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="h-5 w-5 sm:h-6 sm:w-6" /> : <Menu className="h-5 w-5 sm:h-6 sm:w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden bg-black/95 backdrop-blur-md border-t border-gray-800">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="block px-3 py-2 text-white hover:text-loena-gold transition-colors duration-200"
                  onClick={() => {
                    setIsOpen(false);
                    scrollToTop();
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
